import OrderToggleIcon from '@mui/icons-material/Add';
import CountIcon from '@mui/icons-material/BurstModeOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ViewItemsIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/GetApp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import FootprintIcon from '@mui/icons-material/SelectAll';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  Badge,
  Box,
  Button,
  ListItem as MUIListItem,
  ListSubheader as ListSubheaderUI,
  ToggleButton as MUIToggleButton,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import {useTranslation} from 'react-i18next';

const BUTTON_SIZE = theme => theme.spacing(3.75); // NOTE: Uses non-standard quarter-multiple for better design fit

export const StyledBadge = props => (
  <Badge
    color="primary"
    max={99}
    sx={{
      '& .MuiBadge-badge': {
        fontSize: 11,
        minWidth: 18,
        height: 18,
      },
    }}
    {...props}
  />
);

export const ItemTitle = props => (
  <Typography
    variant="subtitle1"
    {...props}
    sx={[
      {
        display: 'block',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

ItemTitle.propTypes = {
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export const GroupTitle = props => (
  <Typography
    variant="subtitle2"
    {...props}
    sx={[
      {
        display: 'inline',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

GroupTitle.propTypes = {
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export const ListSubheader = styled(ListSubheaderUI)(({theme}) => ({
  backgroundColor: theme.palette.common.white,
  lineHeight: 'inherit',
  zIndex: 20,
}));

export const ItemId = props => (
  <Typography
    variant="body2"
    {...props}
    sx={[
      {
        fontFamily: 'monospace',
        fontSize: 13,
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

ItemId.propTypes = {
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};

export const ItemCountIcon = styled(CountIcon)(({theme}) => ({
  marginRight: theme.spacing(0.5),
}));

export const ItemsCount = ({showIcon = true, selected, total, suffix}) => {
  const {t} = useTranslation();

  return (
    <Tooltip
      title={
        selected && selected > 0
          ? t('itemsPanel.tooltipSelected', {selected, total})
          : ''
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          mr: 1,
          color: 'grey.600',
        }}
      >
        {showIcon && <ItemCountIcon color="inherit" fontSize="small" />}
        <Typography
          color="textSecondary"
          variant="caption"
          data-qe="item-count"
          sx={{
            display: 'initial',
          }}
        >
          <Typography
            color="primary"
            variant="caption"
            sx={{
              display: 'inline',
            }}
          >
            {selected > 0 && selected}
          </Typography>
          <span>{selected > 0 ? '/' : ''}</span>
          {total || 0} {suffix ? suffix : ''}
        </Typography>
      </Box>
    </Tooltip>
  );
};

ItemsCount.propTypes = {
  showIcon: PropTypes.bool,
  selected: PropTypes.number,
  total: PropTypes.number,
  suffix: PropTypes.string,
};

export const ListItem = React.forwardRef(
  ({isDragging, isComparing, sx, ...props}, ref) => (
    <MUIListItem
      ref={ref}
      sx={{
        cursor:
          isComparing && !isDragging
            ? 'grab'
            : isDragging
              ? 'grabbing'
              : 'pointer',
        ...sx,
      }}
      container="div"
      slotProps={{
        root: {
          sx: {
            transform: 'translate3d(0, 0, 0)',
            backgroundColor: 'background.paper',
          },
        },
      }}
      // If list item is selected and spacebar is pressed
      // Prevent toggling the item, since we use spacebar
      // in the Explorer map to temporarily hide imagery
      onKeyUp={event => {
        if (event.keyCode === 32) {
          event.preventDefault();
        }
      }}
      {...props}
    />
  )
);
ListItem.propTypes = {
  isDragging: PropTypes.bool,
  isComparing: PropTypes.bool,
  sx: PropTypes.object,
};

export const LoadMoreButton = ({children, ...props}) => {
  const {t} = useTranslation();

  return (
    <Box
      sx={{
        m: 2,
      }}
    >
      <Button
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        {...props}
      >
        {children ? children : t('itemsPanel.buttonLoadMore')}
      </Button>
    </Box>
  );
};
LoadMoreButton.propTypes = {
  children: PropTypes.node,
};

export const RequestAccessButton = props => (
  <Button
    fullWidth
    variant="contained"
    color="primary"
    size="small"
    sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      borderRadius: 0,
      backgroundColor: 'grey.700',
      padding: 0.5,
    }}
    {...props}
  />
);

export const ToggleButton = React.forwardRef(
  ({disabled, onClick, ...props}, ref) => (
    <MUIToggleButton
      sx={{
        height: BUTTON_SIZE,
        width: BUTTON_SIZE,
      }}
      color="primary"
      component={disabled ? 'div' : undefined}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      ref={ref}
      {...props}
    />
  )
);

ToggleButton.propTypes = {
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

export const ViewToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton data-qe="view-on-map-button" ref={ref} {...props}>
    <VisibilityIcon fontSize="small" />
  </ToggleButton>
));

ViewToggleButton.propTypes = {
  selected: PropTypes.bool,
};

export const EyeBadgeIcon = props => {
  return (
    <Box
      data-qe="eye-badge-icon"
      sx={theme => ({
        position: 'absolute',
        top: 2,
        right: 2,
        width: 25,
        height: 25,
        color: 'action.active',
        backgroundColor: 'grey.300',
        ...theme.applyStyles('dark', {
          backgroundColor: 'grey.700',
        }),
        ...(props.isViewSelected
          ? {
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
            }
          : {}),
        borderRadius: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      <VisibilityIcon fontSize="small" />
    </Box>
  );
};

EyeBadgeIcon.propTypes = {
  isViewSelected: PropTypes.bool,
};

export const OrderToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton data-qe="add-to-order-button" ref={ref} {...props}>
    {props.selected ? (
      <CheckIcon fontSize="small" />
    ) : (
      <OrderToggleIcon fontSize="small" />
    )}
  </ToggleButton>
));

OrderToggleButton.propTypes = {
  selected: PropTypes.bool,
};

export const InfoToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <InfoOutlinedIcon fontSize="small" />
  </ToggleButton>
));

export const DownloadToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <DownloadIcon fontSize="small" />
  </ToggleButton>
));

export const ShareToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <ShareIcon fontSize="small" />
  </ToggleButton>
));

export const FootprintToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <FootprintIcon fontSize="small" />
  </ToggleButton>
));

export const LinksToggleButton = React.forwardRef((props, ref) => (
  <ToggleButton ref={ref} {...props}>
    <LinkIcon
      sx={{
        fontSize: 'small',
      }}
    />
  </ToggleButton>
));

export const ViewItemsButton = ({itemCount, ...props}) => {
  const {t} = useTranslation();

  return (
    <Button
      size="small"
      variant="outlined"
      color="primary"
      endIcon={<ViewItemsIcon />}
      sx={{
        height: BUTTON_SIZE,
        py: 0,
        px: 1,
        justifyContent: 'space-between',
        minWidth: 100,
      }}
      aria-label={t(`resultItem.viewItems`, {
        count: itemCount,
      })}
      {...props}
    >
      <span data-qe="item-count">
        {t('resultItem.itemCount', {count: itemCount})}
      </span>
    </Button>
  );
};

ViewItemsButton.propTypes = {
  itemCount: PropTypes.number.isRequired,
};
